/**
 * P2P Context
 *
 * Provides P2P node state throughout the app.
 * Includes Bluetooth mesh controls for offline connectivity.
 */

import { createContext, useContext, useEffect } from 'react'
import type { Libp2p } from 'libp2p'
import type { P2PStatus, AllowedTransportsConfig } from '@/lib/types'
import { useP2P } from '@/hooks/useP2P'

interface P2PContextValue {
  node: Libp2p | null
  status: P2PStatus
  isInitialized: boolean
  error: Error | null
  // Bluetooth mesh
  bluetoothAvailable: boolean
  bluetoothEnabled: boolean
  enableBluetooth: () => Promise<void>
  disableBluetooth: () => void
  scanBluetooth: () => Promise<void>
  // Transport restrictions
  allowedTransports: AllowedTransportsConfig
  setAllowedTransports: (config: AllowedTransportsConfig) => void
}

const P2PContext = createContext<P2PContextValue | null>(null)

export function P2PProvider({ children }: { children: React.ReactNode }) {
  const p2p = useP2P()

  useEffect(() => {
    // Initialize P2P on mount
    p2p.initialize()

    return () => {
      // Cleanup on unmount
      p2p.shutdown()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <P2PContext.Provider
      value={{
        node: p2p.node,
        status: p2p.status,
        isInitialized: p2p.isInitialized,
        error: p2p.error,
        // Bluetooth
        bluetoothAvailable: p2p.bluetoothAvailable,
        bluetoothEnabled: p2p.bluetoothEnabled,
        enableBluetooth: p2p.enableBluetooth,
        disableBluetooth: p2p.disableBluetooth,
        scanBluetooth: p2p.scanBluetooth,
        // Transport restrictions
        allowedTransports: p2p.allowedTransports,
        setAllowedTransports: p2p.setAllowedTransports,
      }}
    >
      {children}
    </P2PContext.Provider>
  )
}

// Hook exported separately to work with Fast Refresh
export function useP2PContext() {
  const context = useContext(P2PContext)
  if (!context) {
    throw new Error('useP2PContext must be used within P2PProvider')
  }
  return context
}
